import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface Partner {
  id: string;
  name: string;
  logo_url: string;
  website_url?: string;
  description?: string;
  order_index: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface CreatePartnerData {
  name: string;
  logo_url: string;
  website_url?: string;
  description?: string;
  order_index: number;
  is_active: boolean;
}

interface UpdatePartnerData {
  name: string;
  logo_url: string;
  website_url?: string;
  description?: string;
  order_index: number;
  is_active: boolean;
}

export const usePartners = () => {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchPartners = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('partners')
        .select('*')
        .order('order_index', { ascending: true });

      if (error) throw error;
      setPartners(data || []);
    } catch (error) {
      console.error('Error fetching partners:', error);
    } finally {
      setLoading(false);
    }
  };

  const createPartner = async (partnerData: CreatePartnerData) => {
    const { data, error } = await supabase
      .from('partners')
      .insert([partnerData])
      .select()
      .single();

    if (error) throw error;
    
    await fetchPartners();
    return data;
  };

  const updatePartner = async (id: string, partnerData: UpdatePartnerData) => {
    const { data, error } = await supabase
      .from('partners')
      .update(partnerData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    
    await fetchPartners();
    return data;
  };

  const deletePartner = async (id: string) => {
    const { error } = await supabase
      .from('partners')
      .delete()
      .eq('id', id);

    if (error) throw error;
    
    await fetchPartners();
  };

  const updatePartnerOrder = async (id: string, direction: 'up' | 'down') => {
    const partner = partners.find(p => p.id === id);
    if (!partner) return;

    const currentIndex = partner.order_index;
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    // Find the partner to swap with
    const swapPartner = partners.find(p => p.order_index === newIndex);
    
    if (swapPartner) {
      // Swap the order indices
      await supabase
        .from('partners')
        .update({ order_index: currentIndex })
        .eq('id', swapPartner.id);

      await supabase
        .from('partners')
        .update({ order_index: newIndex })
        .eq('id', id);

      await fetchPartners();
    }
  };

  useEffect(() => {
    fetchPartners();
  }, []);

  return {
    partners,
    loading,
    createPartner,
    updatePartner,
    deletePartner,
    updatePartnerOrder,
    fetchPartners,
  };
};